"use client";

import { motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import { DynamicIcon } from "@/components/DynamicIcon";
import LocalizedLink from "@/components/LocalizedLink";

export const BADGE_HREF_MAP: Record<string, string> = {
  // FHIR / Standards (Healthcare)
  "FHIR R4 / R5": "/health-tech/fhir-integration",
  "FHIR": "/health-tech/fhir-integration",
  "HL7 v2 & v3": "/health-tech/mirth-connect-integration",
  "HL7": "/health-tech/mirth-connect-integration",
  "SMART on FHIR": "/health-tech/fhir-integration",
  "CDA / C-CDA": "/health-tech/fhir-integration",

  // Compliance & Security (Healthcare)
  "HIPAA / HITECH": "/health-tech/hipaa-compliant-software",
  "HIPAA Compliant": "/health-tech/hipaa-compliant-software",
  "HIPAA-ready": "/health-tech/hipaa-compliant-software",
  "OAuth 2.0 / SAML": "/health-tech/hipaa-compliant-software",

  // Medical Coding & Terminology
  "ICD-10 / CPT Coding": "/health-tech/medical-billing-rcm",
  "SNOMED CT": "/health-tech/healthcare-data-analytics-platform",
  "LOINC": "/health-tech/healthcare-data-analytics-platform",

  // Communications & Telehealth
  "Direct Messaging": "/health-tech/patient-portal-software",
  "Telehealth Built-In": "/health-tech/telemedicine-software",
  "Insurance Claims": "/health-tech/medical-billing-rcm",

  // EdTech / LMS Standards
  "SCORM 1.2 / 2004": "/services/frontend/edutech",
  "xAPI (Tin Can)": "/services/frontend/edutech",
  "LTI 1.3": "/services/frontend/edutech",
  "WCAG 2.1 AA": "/services/frontend/edutech",
  "GDPR Compliant": "/privacy-policy",
  "GDPR-ready": "/privacy-policy",
  "SSO / SAML": "/services/frontend/edutech",
  "OAuth 2.0": "/services/frontend/edutech",
  "REST APIs": "/services/backend",
  "Webhooks": "/services/backend",
  "Multi-tenancy": "/services/frontend/edutech",
};

export const COMPLIANCE_HREF_MAP: Record<string, string> = {
  // Healthcare
  "HIPAA / HITECH": "/health-tech/hipaa-compliant-software",
  "FHIR R4 / R5": "/health-tech/fhir-integration",
  "HL7 v2 & v3": "/health-tech/mirth-connect-integration",
  "ONC Health IT Cert": "/health-tech/fhir-integration",
  "SOC 2 Audit Support": "/trust-center",
  "SOC-2-Audit-Unterstützung": "/trust-center",
  "Accompagnement audit SOC 2": "/trust-center",
  "21st Century Cures": "/health-tech/fhir-integration",
  "GDPR (Int'l Projects)": "/privacy-policy",

  // EdTech / LMS
  "SCORM 1.2 / 2004": "/services/frontend/edutech",
  "xAPI (Tin Can)": "/services/frontend/edutech",
  "LTI 1.3": "/services/frontend/edutech",
  "WCAG 2.1 AA": "/services/frontend/edutech",
  "GDPR Compliant": "/privacy-policy",
  "GDPR-ready": "/privacy-policy",
  "COPPA (K-12)": "/services/frontend/edutech",
  "FERPA (Education)": "/services/frontend/edutech",
  "ISO 27001": "/services/frontend/edutech",
};

export const TECH_STACK_HREF_MAP: Record<string, string> = {
  // Healthcare
  "HAPI FHIR": "/health-tech/fhir-integration",
  "Azure Health Data": "/services/cloud-devops-engineering",
  "AWS HealthLake": "/services/cloud-devops-engineering",

  // General / shared
  "React / Next.js": "/services/frontend",
  "Node.js": "/services/backend",
  "Python / Django": "/services/backend",
  "Kubernetes": "/services/devops",
  "Terraform": "/services/devops",
  "PostgreSQL": "/services/backend",
  "MongoDB": "/services/backend",
  "Redis": "/services/backend",
  "AWS / Azure / GCP": "/services/cloud-devops-engineering",
  "Docker": "/services/devops",
  "Elasticsearch": "/services/backend",
  "WebRTC (Video)": "/services/mobile-app-development-services",
  "Stripe / PayPal": "/services/backend",
  "Zoom / Teams API": "/services/mobile-app-development-services",
  "S3 / CDN": "/services/cloud-devops-engineering",
  "GitHub Actions": "/services/devops",
};

export const FEATURE_TITLE_HREF_MAP: Record<string, string> = {
  "Secure Data Migration": "/health-tech/healthcare-data-migration",
  "Practice Management": "/health-tech/practice-management-software",
  "Telehealth & Portals": "/health-tech/patient-portal-software",
  "Revenue Cycle Management": "/health-tech/medical-billing-rcm",
  "Reporting & Insights": "/health-tech/healthcare-data-analytics-platform",
  "HIPAA-Compliant by Design": "/health-tech/hipaa-compliant-software",
  "FHIR-Native APIs": "/health-tech/fhir-integration",
};

export const TAG_HREF_MAP: Record<string, string> = {
  "Video Visits": "/health-tech/telemedicine-software",
  "Online Intake": "/health-tech/intake-forms-software",
  "Auto Reminders": "/health-tech/appointment-scheduling-software",
  "Auto Claims": "/health-tech/medical-billing-rcm",
  "Quality Metrics": "/health-tech/healthcare-data-analytics-platform",
};

function renderMarkdownText(text: string) {
  if (!text || typeof text !== "string") return text;
  const parts: (string | React.ReactNode)[] = [];
  let lastIndex = 0;
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    const [, label, url] = match;
    parts.push(
      <LocalizedLink
        key={match.index}
        href={url}
        className="font-medium text-orange-700 dark:text-orange-400 underline decoration-orange-500/40 underline-offset-4 transition-colors hover:text-orange-800 dark:hover:text-orange-300 hover:decoration-orange-600"
      >
        {label}
      </LocalizedLink>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}

interface FeatureSectionProps {
  data: {
    title: string;
    boldTitle?: string;
    description: string;
    features: { icon?: string; title: string; description: string; tags?: string[]; href?: string }[];
    badges?: (string | { name: string; href?: string })[];
    compliance?: Array<{ name: string; status: string; href?: string }>;
    techStack?: string[];
  };
}

const EASE = [0.16, 1, 0.3, 1] as const;

export function FeatureSection({ data }: FeatureSectionProps) {
  const prefersReduced = useReducedMotion();

  if (!data || !data.features || data.features.length === 0) return null;

  return (
    <section className="relative z-10 bg-transparent px-5 py-24 text-foreground sm:px-8 md:py-36 lg:px-12">
      <div className="mx-auto max-w-[88rem]">
        <div className="max-w-2xl">
          <h2 className="font-display text-balance text-3xl font-semibold leading-[1.08] tracking-[-0.02em] md:text-4xl">
            {data.title}
            {data.boldTitle && <> {data.boldTitle}</>}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-foreground/80 md:text-lg">
            {renderMarkdownText(data.description)}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-x-16 gap-y-10 md:grid-cols-2 lg:grid-cols-3">
          {data.features.map((feature, index) => {
            const titleHref = feature.href || FEATURE_TITLE_HREF_MAP[feature.title];
            return (
              <motion.div
                key={feature.title}
                className="border-t border-border pt-8"
                initial={prefersReduced ? false : { opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease: EASE, delay: (index % 3) * 0.1 }}
              >
                <div className="flex items-center gap-3">
                  {typeof feature.icon === "string" && (
                    <DynamicIcon
                      name={feature.icon}
                      size={20}
                      strokeWidth={1.75}
                      className="flex-shrink-0 text-orange-700 dark:text-orange-400"
                      aria-hidden="true"
                    />
                  )}
                  <h3 className="font-display text-xl font-semibold tracking-[-0.01em] text-foreground">
                    {titleHref ? (
                      <LocalizedLink
                        href={titleHref}
                        className="transition-colors hover:text-orange-500 hover:underline"
                      >
                        {feature.title}
                      </LocalizedLink>
                    ) : (
                      feature.title
                    )}
                  </h3>
                </div>
                <p className="mt-3 max-w-lg text-base leading-relaxed text-foreground/80">
                  {renderMarkdownText(feature.description)}
                </p>
                {feature.tags && feature.tags.length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-foreground/60">
                    {feature.tags.map((tag, tagIdx) => {
                      const tagHref = TAG_HREF_MAP[tag];
                      return (
                        <span key={tag} className="inline-flex items-center">
                          {tagIdx > 0 && <span className="mr-2 select-none text-foreground/30">·</span>}
                          {tagHref ? (
                            <LocalizedLink
                              href={tagHref}
                              className="transition-colors hover:text-orange-500 hover:underline"
                            >
                              {tag}
                            </LocalizedLink>
                          ) : (
                            tag
                          )}
                        </span>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {data.badges && data.badges.length > 0 && (
          <div className="mt-16 flex flex-wrap gap-2 border-t border-border pt-8">
            {data.badges.map((badge, idx) => {
              const name = typeof badge === "string" ? badge : badge.name;
              const href =
                typeof badge === "string"
                  ? BADGE_HREF_MAP[name]
                  : badge.href || BADGE_HREF_MAP[name];

              if (href) {
                return (
                  <LocalizedLink
                    key={name || idx}
                    href={href}
                    className="inline-flex items-center rounded-full border border-border px-3.5 py-1.5 text-xs font-medium text-foreground/70 transition-all hover:border-orange-500 hover:bg-orange-500/10 hover:text-orange-500 cursor-pointer"
                  >
                    {name}
                  </LocalizedLink>
                );
              }

              return (
                <span
                  key={name || idx}
                  className="rounded-full border border-border px-3.5 py-1.5 text-xs font-medium text-foreground/70"
                >
                  {name}
                </span>
              );
            })}
          </div>
        )}

        {data.techStack && data.techStack.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {data.techStack.map((tech) => {
              const href = TECH_STACK_HREF_MAP[tech];
              if (href) {
                return (
                  <LocalizedLink
                    key={tech}
                    href={href}
                    className="rounded-full border border-orange-500/20 bg-orange-500/5 px-3.5 py-1.5 text-xs font-medium text-orange-700 dark:text-orange-400 transition-all hover:bg-orange-500/15 hover:border-orange-500/40 cursor-pointer"
                  >
                    {tech}
                  </LocalizedLink>
                );
              }
              return (
                <span
                  key={tech}
                  className="rounded-full border border-orange-500/20 bg-orange-500/5 px-3.5 py-1.5 text-xs font-medium text-orange-700 dark:text-orange-400"
                >
                  {tech}
                </span>
              );
            })}
          </div>
        )}

        {data.compliance && data.compliance.length > 0 && (
          <div className="mt-8 grid grid-cols-1 divide-y divide-border border-y border-border sm:grid-cols-2 sm:divide-x sm:divide-y-0">
            {data.compliance.map((item) => {
              const href = item.href || COMPLIANCE_HREF_MAP[item.name];
              return (
                <div
                  key={item.name}
                  className="flex items-center justify-between gap-4 px-4 py-3 first:pl-0"
                >
                  {href ? (
                    <LocalizedLink
                      href={href}
                      className="text-sm text-foreground/80 transition-colors hover:text-orange-500 hover:underline"
                    >
                      {item.name}
                    </LocalizedLink>
                  ) : (
                    <span className="text-sm text-foreground/80">{item.name}</span>
                  )}
                  <span className="flex flex-shrink-0 items-center gap-1.5 text-xs font-medium text-orange-700 dark:text-orange-400">
                    <Check size={13} strokeWidth={2.5} aria-hidden="true" />
                    {item.status}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
